import { inject, reactive, toRefs } from "vue";
import { UserType } from '@/domain/entities/UserTypes';
import { Patient } from '@/domain/entities/Patient';
import { db } from '@/infraestructure/datasource'
import { Doctor } from '@/domain/entities/Doctor';
import { PatientRepository } from '@/infraestructure/patient/PatientRepository';
import { DoctorRepository } from '@/infraestructure/doctor/DoctorRepository';
import useAuth from './AuthState';
import { pprint } from '@/helpers/pprint';
import { DiaryQuestion } from '@/domain/entities/DiaryQuestion';
import { isPatient } from '@/domain/entities/User';
import { DateTime } from 'luxon'
// import { pprint } from '@/helpers/pprint';
const { user: authUser } = useAuth()

let patientRepository: PatientRepository;
let doctorRepository: DoctorRepository;

interface UserState {
	typeUser: UserType | null,
	user: Patient | Doctor | null,
	diaryQuestions: DiaryQuestion[] | null,
}

const state = reactive<UserState>({
	typeUser: null,
	user: null,
	diaryQuestions: null
})

const fetchHasFilledInformation = async (userId: string) => {
	let patientDoc = await db.collection('patients').doc(userId).get()
	state.user = patientDoc.data() as Patient
	let hasFilled = (patientDoc.data() as Patient).hasFilledPersonalInformation
	return hasFilled
}

let closeSnapshot: () => void | undefined;
const onUserDataChange = () => {
	let id = authUser.value!.id
	closeSnapshot = db.collection('patients').doc(id).onSnapshot(
		(doc) => {
			pprint('listen on change')
			let newStatePatient: Patient = doc.data() as Patient
			state.user = newStatePatient
		}
	)
}
const closeUserDataChange = () => {
	if (closeSnapshot !== undefined) closeSnapshot()
}

const fetchUserData = async () => {
	let id = authUser.value!.id
	let type = authUser.value!.typeUser
	switch (type) {
		case UserType.Doctor:
			state.user = await doctorRepository!.fetchUserData(id) as Doctor
			break;
		case UserType.Patient:
			state.user = await patientRepository!.fetchPersonalInformation(id, false) as Patient
			break;
		default:
			throw 'User not identified'
	}
}

const MaxDiaryDays = 10
const fetchDiaryQuestions = async () => {
	let questions = state.diaryQuestions;
	if (questions === null) {
		state.diaryQuestions = await patientRepository.fetchDiaryQuestions()
		questions = state.diaryQuestions
	}

	if (isPatient(state.user)) {
		let diaries = state.user.diaries || {}
		let createdAt = (state.user.createdAt as any).toDate()
		let today = new Date()
		var difference = today.getTime() - createdAt.getTime();
		var days = Math.ceil(difference / (1000 * 3600 * 24));
		for (let i = 0; i < days; i++) {
			let key = DateTime.local().minus({days: i}).toFormat('dd/MM/y')
			if (diaries[key] === undefined) {
				diaries[key] = []
			}
		}

		for (const dateKey in diaries) {
			for (let questionResponse of diaries[dateKey]) {
				let question = questions.find(q => q.id === questionResponse.questionId)
				questionResponse.questionLabel = question?.question
			}
			for (let q of questions) {
				let hasQuestion = diaries[dateKey].find(a => a.questionId == q.id) !== undefined
				if (!hasQuestion) {
					diaries[dateKey].push({ id: '', questionId: q.id, answer: '', questionLabel: q.question })
				}
			}
		}
		(state.user as Patient).diaries = diaries
	}
}
function updateDiary() {
	patientRepository.updateDiary(state.user!.id, (state.user as Patient).diaries!)
}


async function sendLikertSurvey(scoreIndex: number) {
	await patientRepository.addLikertSurvey(state.user as Patient, scoreIndex + 1)
}

const ONE_HOUR = 1 * (60 * 60 * 1000)
const lastTimeOpenLikertKey = 'patient/last_time_open'
function shouldOpenLikert() {
	const lastTimeOpen = JSON.parse(window.localStorage.getItem(lastTimeOpenLikertKey) || 'null')
	if (!lastTimeOpen) return true
	const now = new Date().getTime()
	return (now - ONE_HOUR >= lastTimeOpen)
}
function updateLastTimeOpened() {
	window.localStorage.setItem(lastTimeOpenLikertKey, JSON.stringify(new Date().getTime()))
}

export default function useUser() {
	patientRepository = inject<PatientRepository>(PatientRepository.key)!
	doctorRepository = inject<DoctorRepository>(DoctorRepository.key)!
	if (state.user === null) {
		fetchUserData()
	}
	pprint('*** using useUser')

	return {
		...toRefs(state), fetchHasFilledInformation, fetchUserData,
		onUserDataChange, closeUserDataChange,
		fetchDiaryQuestions, updateDiary, sendLikertSurvey,
		shouldOpenLikert, updateLastTimeOpened,
		MaxDiaryDays,
	}
}
